import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Alert,
  List,
  ListItem,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Home,
  CloudUpload,
  Storage
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const DataLoadInstructions: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission (after loading completes)
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Data Load Instructions</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudUpload sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Data Extract, Upload & Publish
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Data management documentation
          </Typography>
        </Box>

        {/* Tabbed Content */}
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="data management tabs">
              <Tab label="Upload MarketCompass Reports" />
              <Tab label="CoStar Extract/Import" />
              <Tab label="Update Private-DB-Dev Market Info" />
              <Tab label="Publish Market Info to Prod" />
            </Tabs>

            {/* Tab 1: Upload MarketCompass Reports */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                MarketCompass Report Upload Instructions
              </Typography>

              {/* Upload MarketCompass Reports */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Upload PDF Reports for AI Analysis & Data Extraction</Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Upload MarketCompass PDF reports for AI-powered analysis and data extraction into the database.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      What This Script & API Processing Does:
                    </Typography>
                    <List dense sx={{ mt: 0 }}>
                      <ListItem sx={{ py: 0 }}>• Validates and prepares PDF files from the input directory</ListItem>
                      <ListItem sx={{ py: 0 }}>• Extracts market names from filenames and matches to database markets</ListItem>
                      <ListItem sx={{ py: 0 }}>• Uploads PDFs to the AI processing endpoint</ListItem>
                      <ListItem sx={{ py: 0 }}>• <strong>Extracts text content from the PDF reports</strong></ListItem>
                      <ListItem sx={{ py: 0 }}>• <strong>Uses LLM/AI to analyze report content and generate:</strong></ListItem>
                      <ListItem sx={{ py: 0, pl: 4 }}>- Key findings and insights from the report</ListItem>
                      <ListItem sx={{ py: 0, pl: 4 }}>- Market descriptions and summaries</ListItem>
                      <ListItem sx={{ py: 0, pl: 4 }}>- Structured data points and metrics</ListItem>
                      <ListItem sx={{ py: 0, pl: 4 }}>- Categorized content for database storage</ListItem>
                      <ListItem sx={{ py: 0 }}>• Stores all extracted and generated content in the database</ListItem>
                      <ListItem sx={{ py: 0 }}>• Monitors processing progress in real-time</ListItem>
                      <ListItem sx={{ py: 0 }}>• Moves completed PDFs to "completed" folder for archival</ListItem>
                    </List>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Processing time: 1-2 minutes per PDF (includes AI analysis and content generation).
                    </Typography>
                  </Alert>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Navigate to Script Directory
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      cd data-uploads/import-market-reports/
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Prepare PDF Files
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Place PDF files in the "input" folder (or specify a different directory when prompted).
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      # Create input directory if needed<br/>
                      mkdir -p input<br/>
                      <br/>
                      # Copy PDF files to input directory<br/>
                      cp /path/to/reports/*.pdf input/
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Run the Upload Script
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      ./run-marketcompass-upload-new.sh<br/>
                      <br/>
                      # Script will prompt for:<br/>
                      # 1. Source directory (default: input)<br/>
                      # 2. Target environment (1=DEV, 2=PROD)<br/>
                      # 3. Confirmation to proceed<br/>
                      <br/>
                      # The script will then:<br/>
                      # - Validate directory and check for PDFs<br/>
                      # - Load API settings from local.settings.json<br/>
                      # - Process each PDF file<br/>
                      # - Show real-time progress<br/>
                      # - Move completed files to input/completed/
                    </Typography>
                  </Box>

                  <Alert severity="warning">
                    <strong>Important:</strong> Ensure local.settings.json is configured with API_BASE_URL_DEV, API_BASE_URL_PROD, and API_KEY before running.
                  </Alert>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Tab 2: CoStar Extract/Import */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                CoStar Data Extraction & Import Instructions
              </Typography>

              {/* Step 1: Extract Data from CoStar */}
              <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Step 1: Extract Data from CoStar</Typography>
                <Chip label="run-extracts.sh" size="small" sx={{ ml: 2 }} />
              </Box>
              
              <Typography variant="body1" paragraph>
                Use the extraction script to download Market Analytics and Properties data from CoStar.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                A. First-time Setup - Authentication
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                <Typography variant="body2">
                  ./run-extracts.sh<br/>
                  # Choose option 1 (Authenticate) or 2 (Authenticate without MFA)<br/>
                  # Select user: Brad (1) or Bryan (2)<br/>
                  # Enter 2FA code when prompted
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Authentication state is saved to auth/costar-auth.json and persists across sessions
              </Alert>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                B. Extract Market Analytics
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                <Typography variant="body2">
                  ./run-extracts.sh<br/>
                  # Choose option 5 (Extract Market Analytics)<br/>
                  # Press Enter to confirm<br/>
                  # Process extracts data for all countries
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Downloads Excel files to: extracts_market_analytics/
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                C. Extract Properties
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                <Typography variant="body2">
                  ./run-extracts.sh<br/>
                  # Choose option 4 (Extract Properties)<br/>
                  # Select extraction scope:<br/>
                  #   1) Specific country (e.g., "United States")<br/>
                  #   2) Target countries (US, AU, UK, DE, FR, ES)<br/>
                  #   3) All countries (several hours)
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Downloads Excel files to: extracts_properties/[Country]/[Market]/
              </Typography>

              <Alert severity="warning">
                <strong>Note:</strong> Property extraction for all countries can take 4-6 hours. Consider running target countries first.
              </Alert>
            </CardContent>
          </Card>

          {/* Step 2: Import Data to SQL Server */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Storage sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Step 2: Import Data to SQL Server</Typography>
                <Chip label="run-imports.sh" size="small" sx={{ ml: 2 }} />
              </Box>
              
              <Typography variant="body1" paragraph>
                Import the extracted data into the SQL Server database.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Interactive Mode
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                <Typography variant="body2">
                  ./run-imports.sh<br/>
                  # Choose from menu:<br/>
                  #   1) Market Analytics - All Countries<br/>
                  #   2) Market Analytics - Specific Country<br/>
                  #   3) Properties only<br/>
                  #   4) Exit
                </Typography>
              </Box>

              <Alert severity="info">
                The import process automatically:
                <List dense>
                  <ListItem>• Converts Excel files to CSV format</ListItem>
                  <ListItem>• Consolidates multiple files into a single import</ListItem>
                  <ListItem>• Uses BCP for efficient bulk loading</ListItem>
                  <ListItem>• Logs all operations to scripts/.tmp/</ListItem>
                </List>
              </Alert>
            </CardContent>
          </Card>

          {/* Step 3: Copy Data from Temp to Final Tables */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Storage sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Step 3: Copy Data from Temp to Final Tables</Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                After successful import, copy the data from temporary/staging tables to the final production tables.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Run Stored Procedures
              </Typography>
              <Typography variant="body2" paragraph>
                Execute the following stored procedures in order to populate the final tables:
              </Typography>
              
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                <Typography variant="body2">
                  -- 1. Copy Market Analytics data from temp to final table<br/>
                  EXEC sp_populate_market_analytics_from_temp;<br/>
                  <br/>
                  -- 2. Copy Properties data from staging to final table<br/>
                  EXEC sp_populate_properties_with_geography;
                </Typography>
              </Box>

              <Alert severity="info">
                <strong>Note:</strong> These stored procedures will replace existing data in the final tables. Ensure the import was successful before running them.
              </Alert>
            </CardContent>
          </Card>
            </TabPanel>

            {/* Tab 3: Update Private-DB-Dev Market Info */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Market Description Generator Instructions
              </Typography>

              {/* Run Market Description Generator */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Storage sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">Generate Market Descriptions</Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Run the market description generator to update descriptions in the Private-DB-Dev database.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      What This Script Does:
                    </Typography>
                    <List dense sx={{ mt: 0 }}>
                      <ListItem sx={{ py: 0 }}>• Connects to the Private-DB-Dev database</ListItem>
                      <ListItem sx={{ py: 0 }}>• Reads market data from the database (names, locations, statistics)</ListItem>
                      <ListItem sx={{ py: 0 }}>• Generates AI-powered descriptions for each market using OpenAI</ListItem>
                      <ListItem sx={{ py: 0 }}>• Creates market overviews, highlights, and key features</ListItem>
                      <ListItem sx={{ py: 0 }}>• Updates the market_descriptions table with new content</ListItem>
                      <ListItem sx={{ py: 0 }}>• Preserves any manually edited descriptions (skips markets with existing descriptions)</ListItem>
                    </List>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Processing time: Approximately 2-5 minutes depending on the number of markets.
                    </Typography>
                  </Alert>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Navigate to Script Directory
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      cd data-uploads/marketDescriptionsFromData/
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Run the Generator Script
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      ./run.sh<br/>
                      <br/>
                      # The script will:<br/>
                      # 1. Check if Node.js is installed<br/>
                      # 2. Verify api-private is running on port 7073<br/>
                      #    (prompts to continue if not detected)<br/>
                      # 3. Execute generate-market-descriptions.js<br/>
                      # 4. Update market descriptions in Private-DB-Dev
                    </Typography>
                  </Box>

                  <Alert severity="warning">
                    <strong>Important:</strong> This process updates the Private-DB-Dev database. The changes will need to be published to production using the next tab's instructions.
                  </Alert>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Tab 4: Publish Market Info to Prod */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                Data Publishing Instructions
              </Typography>

              {/* Run Data Publishing Tool */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Publish Data from DEV to PROD</Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Sync market descriptions and other data from the DEV database to the PROD environment.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Data Published to Production:
                    </Typography>
                    <List dense sx={{ mt: 0 }}>
                      <ListItem sx={{ py: 0 }}>• Market descriptions and overview text</ListItem>
                      <ListItem sx={{ py: 0 }}>• Key market highlights and features</ListItem>
                      <ListItem sx={{ py: 0 }}>• Continent, country, and market metadata</ListItem>
                      <ListItem sx={{ py: 0 }}>• Geographic boundaries and coordinates</ListItem>
                      <ListItem sx={{ py: 0 }}>• Market categorization and tags</ListItem>
                      <ListItem sx={{ py: 0 }}>• Last updated timestamps</ListItem>
                    </List>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Note: Market analytics data (ADR, RevPAR, occupancy) is handled separately in Tab 1.
                    </Typography>
                  </Alert>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Navigate to Publishing Directory
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      cd data-publishing/
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Configure Settings (First Time Only)
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Ensure local.settings.json is properly configured with PROD API endpoints and keys.
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      # If local.settings.json doesn't exist, it will be<br/>
                      # created from the template. Edit it with your settings.
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Run the Publishing Script
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                    <Typography variant="body2">
                      ./run.sh<br/>
                      <br/>
                      # The script will:<br/>
                      # 1. Check if Node.js is installed<br/>
                      # 2. Verify local.settings.json exists<br/>
                      # 3. Check PROD settings are configured<br/>
                      # 4. Execute publish-to-prod.js<br/>
                      # 5. Sync data from DEV to PROD
                    </Typography>
                  </Box>

                  <Alert severity="warning">
                    <strong>Caution:</strong> This publishes data directly to the production environment. All market descriptions and metadata will be immediately visible to users. Ensure the DEV data has been reviewed and approved before publishing.
                  </Alert>
                </CardContent>
              </Card>
            </TabPanel>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};